import { useApolloClient } from "@apollo/client";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  AssessmentNominationFragment,
  GetAssessmentOpenForNominationQuery,
  GetAssessmentOpenForNominationQueryVariables,
} from "types/graphql-schema";

import AppLink from "@components/link/link";

import getAssessmentOpenForNominationQuery from "../graphql/get-assessment-open-for-nomination-query";

/**
 * Where a peer review waiting for its peers is unblocked: the nomination dialog for that
 * person, in that review.
 *
 * A real link to /assessments, so a middle click or a new tab still lands on the reader's
 * review tasks. A plain click stays on the page: it loads the nominations the reader may
 * pick for this person and hands them to `onOpen`, which opens the dialog. When there is
 * nothing to open (the review closed for nominations since the table loaded, or the
 * request failed), the click goes to /assessments as the link says.
 */
const NominatePeersLink = ({
  complianceProgramId,
  targetId,
  onOpen,
}: {
  complianceProgramId: number;
  targetId: number;
  onOpen: (assessmentNomination: AssessmentNominationFragment) => void;
}) => {
  const client = useApolloClient();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  // The reader may close the popover before the answer comes back. Then they no longer
  // asked for the dialog, and a late jump to /assessments would surprise them.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    if (loading) {
      return;
    }
    setLoading(true);
    client
      .query<
        GetAssessmentOpenForNominationQuery,
        GetAssessmentOpenForNominationQueryVariables
      >({
        query: getAssessmentOpenForNominationQuery,
        variables: { complianceProgramId, targetId },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => data.assessmentsOpenForNominations.edges[0]?.node)
      .catch(() => null)
      .then((assessmentNomination) => {
        if (!isMounted.current) {
          return;
        }
        setLoading(false);
        if (assessmentNomination) {
          onOpen(assessmentNomination);
        } else {
          history.push("/assessments");
        }
      });
  };

  return (
    <AppLink
      to="/assessments"
      onClick={handleClick}
      aria-busy={loading}
      className="text-sm text-blue-600 hover:underline"
    >
      Nominate peers
    </AppLink>
  );
};

export default NominatePeersLink;
